import { useForm } from '@abyss/web/hooks/useForm';
import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { Accordion } from '@abyss/web/ui/Accordion';
import { Button } from '@abyss/web/ui/Button';
import { Checkbox } from '@abyss/web/ui/Checkbox';
import { CheckboxGroup } from '@abyss/web/ui/CheckboxGroup';
import { Divider } from '@abyss/web/ui/Divider';
import { Flex } from '@abyss/web/ui/Flex';
import { FormProvider } from '@abyss/web/ui/FormProvider';
import { Heading } from '@abyss/web/ui/Heading';
import { RadioGroup } from '@abyss/web/ui/RadioGroup';
import { Text } from '@abyss/web/ui/Text';
import { t } from 'i18next';
import find from 'lodash/find';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { adobeLinkTrackEvent } from '../../../common/AdobeTagging/adobeLinkTrackEvent';
import { Constants } from '../../../common/Constants';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { phoneOnly } from '../../../common/ConstantsStyles';
import { ContentWrapper } from '../../../common/ContentWrapper';
import { isUmrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../context/PortalContext';
import { useConfig } from '../../../frontends/ProviderSearch/context/Analytics';
import { getLanguage } from '../../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useLagoon } from '../../../hooks/useLagoon';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import { removeSpecialCharactersUsingMatch } from '../../../utils/smartChoice.utils';
import { properties } from './OnboardingForm';
import {
  AccordionContainer,
  ClearAllButton,
  HeadingText,
  StyledDrawer,
  StyledMarkdown,
  ToggleSwitchField,
} from './SmartChoice.styled';
import {
  countDifferences,
  countPreferences,
  formatPreferenceData,
  getLanguagesSpokenValues,
  getSelectedGender,
  getSelectedLanguage,
} from './utils';
import { VirtualizedListCheckbox } from './VirtualizedListCheckbox';

export interface RecommendationProp {
  createdDate?: string;
  distance?: string;
  evngApptAvbl?: string;
  hasDismissedCoachMark?: string;
  hasDismissedCoachMarkWeb?: string;
  hasSetPreferences?: string;
  lastUpdatedDate?: string;
  onlineApptSchdlAvbl?: string;
  providerBoardCertStatus?: string;
  providerGender?: string;
  uuid?: string;
  virtualApptAvbl?: string;
  wkndApptAvbl?: string;
  languagesSpoken?: string[];
  hasDefaultPreferences?: string;
  memberRandomizedKey?: string;
}
export interface Props {
  isOpen: boolean;
  handleCloseDrawer: () => void;
  onClickSaveAndUpdate: ({}) => void;
  preferencesData: RecommendationProp;
  preferencesFormFromProviderDetail: boolean;
}

export const getUserSelections = async (form, currentSelectedLanguage) => {
  let userSelections = '';
  if (form?.distance) {
    userSelections += `Distance with in: ${form?.distance} miles | `;
  }
  if (form?.virtualApptAvbl) {
    userSelections += `Virtual appointments | `;
  }
  if (form?.onlineApptSchdlAvbl) {
    userSelections += `Online appointment scheduling | `;
  }
  if (form?.evngApptAvbl) {
    userSelections += `Evening appointments | `;
  }
  if (form?.wkndApptAvbl) {
    userSelections += `Weekend appointments | `;
  }
  if (form?.languagesSpoken?.length) {
    const selectedLanguages = await getSelectedLanguage(
      form?.languagesSpoken,
      currentSelectedLanguage
    );
    userSelections += `Languages spoken: ${selectedLanguages
      ?.map((item) => removeSpecialCharactersUsingMatch(item ?? ''))
      ?.join(';')} | `;
  }
  if (form?.providerGender?.length) {
    userSelections += `Gender: ${getSelectedGender(
      form?.providerGender,
      genderList
    )?.join(';')} | `;
  }
  if (form?.providerBoardCertStatus) {
    userSelections += `Board certified: on | `;
  }
  if (userSelections?.endsWith(' | ')) {
    userSelections = userSelections?.slice(0, -2);
  }
  return userSelections;
};

const distanceMiles = ['1', '5', '10', '25', '50', '100', '120'];
const genderList = [
  { value: 'M', title: 'Male' },
  { value: 'F', title: 'Female' },
];

export const PreferencesForm = ({
  isOpen,
  handleCloseDrawer,
  preferencesData,
  onClickSaveAndUpdate,
  preferencesFormFromProviderDetail,
}: Props) => {
  const mobileScreen = useMediaQuery(phoneOnly);
  const [preferenceCount, setPreferenceCount] = useState(0);
  const [disabledSubmit, setDisabledSubmit] = useState(0);
  const [languagesSpoken, setLanguagesSpoken] = useState<
    { title: string; value: string }[]
  >([]);

  const lagoonData = useLagoon('ui-messaging')();

  const PREFERENCES_FORM_HEADING_DESCRIPTION = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_HEADING_DESCRIPTION,
  });
  const PREFERENCES_FORM_PREFERENCES_APPLIED = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_PREFERENCES_APPLIED,
  });
  const PREFERENCES_FORM_CONVENIENCE_TITLE_DESC = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_CONVENIENCE_TITLE_DESC,
  });
  const PREFERENCES_FORM_DISTANCE_WITHIN = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_DISTANCE_WITHIN,
  });
  const PREFERENCES_FORM_VIRTUAL_APPOINTMENTS = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_VIRTUAL_APPOINTMENTS,
  });
  const PREFERENCES_FORM_ONLINE_APPOINTMENT_SCHEDULING = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_ONLINE_APPOINTMENT_SCHEDULING,
  });
  const PREFERENCES_FORM_EVENING_APPOINTMENTS = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_EVENING_APPOINTMENTS,
  });
  const PREFERENCES_FORM_WEEKEND_APPOINTMENTS = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_WEEKEND_APPOINTMENTS,
  });
  const PREFERENCES_FORM_PERSONAL_FIT_TITLE_DESC = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_PERSONAL_FIT_TITLE_DESC,
  });
  const PREFERENCES_FORM_LANGUAGES_SPOKEN = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_LANGUAGES_SPOKEN,
  });
  const PREFERENCES_FORM_GENDER = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_GENDER,
  });
  const PREFERENCES_FORM_BOARD_CERTIFIED = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_BOARD_CERTIFIED,
  });
  const PREFERENCES_FORM_SEARCH_PROVIDER = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_SEARCH_PROVIDER,
  });
  const PREFERENCES_FORM_NO_PREFERENCES_SET = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_NO_PREFERENCES_SET,
  });
  const PREFERENCES_FORM_CLEAR_ALL = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_CLEAR_ALL,
  });
  const PREFERENCES_FORM_WITHIN = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_WITHIN,
  });
  const PREFERENCES_FORM_MILE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_MILE,
  });
  const PREFERENCES_FORM_MILES = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.PREFERENCES_FORM_MILES,
  });
  const PREFERENCES_FORM_UPDATE_PROVIDER_SCORE = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
      .PREFERENCES_FORM_UPDATE_PROVIDER_SCORE,
  });

  const UPDATE_SEARCH_RESULTS = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.UPDATE_SEARCH_RESULTS,
  });

  const SMART_CHOICE_PES_LANG_CODES = useConfig(
    ConstantsLagoon.CONFIG.SMART_CHOICE_PES_LANG_CODES
  );
  const isSuperUser =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.SUPER_USER) || false;

  const { code: currentSelectedLanguage } = getLanguage();

  const form = useForm({
    defaultValues: {
      distance: null,
      virtualApptAvbl: false,
      onlineApptSchdlAvbl: false,
      evngApptAvbl: false,
      wkndApptAvbl: false,
      // call_support: false,
      languagesSpoken: [],
      providerGender: [],
      providerBoardCertStatus: false,
    },
    values: formatPreferenceData(preferencesData),
  });

  const { search } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state.typeaheadSearchStore.search,
    }))
  );
  const formValues = useMemo(() => form.getValues(), [form.getValues()]);

  useEffect(() => {
    if (formValues) {
      const formFields = JSON.parse(JSON.stringify(form.getValues()));
      const preferencesDataFields = JSON.parse(
        JSON.stringify(formatPreferenceData(preferencesData))
      );

      const difference = countDifferences(formFields, preferencesDataFields);
      const enabledPreferences = countPreferences(formFields);
      setDisabledSubmit(difference);
      setPreferenceCount(enabledPreferences);
    }
  }, [formValues]);

  useEffect(() => {
    const fetchLanguages = async () => {
      const LANGUAGES_SPOKEN = await getLanguagesSpokenValues(
        currentSelectedLanguage,
        SMART_CHOICE_PES_LANG_CODES
      );
      setLanguagesSpoken(LANGUAGES_SPOKEN);
    };

    fetchLanguages();
  }, [currentSelectedLanguage, SMART_CHOICE_PES_LANG_CODES]);

  const onSubmit = async () => {
    const {
      distance,
      virtualApptAvbl,
      onlineApptSchdlAvbl,
      evngApptAvbl,
      wkndApptAvbl,
      languagesSpoken,
      providerGender,
      providerBoardCertStatus,
    } = formValues;
    const payload = {
      uuid: preferencesData?.uuid,
      hasSetPreferences: 'y',
      hasDismissedCoachMark: preferencesData?.hasDismissedCoachMark,
      hasDismissedCoachMarkWeb: 'y',
      distance: distance || '',
      virtualApptAvbl: virtualApptAvbl ? 'y' : 'n',
      onlineApptSchdlAvbl: onlineApptSchdlAvbl ? 'y' : 'n',
      evngApptAvbl: evngApptAvbl ? 'y' : 'n',
      wkndApptAvbl: wkndApptAvbl ? 'y' : 'n',
      providerBoardCertStatus: providerBoardCertStatus ? 'y' : 'n',
      providerGender: providerGender?.length ? providerGender?.join(',') : '',
      languagesSpoken: languagesSpoken?.length
        ? languagesSpoken?.join(',')
        : '',
      isSuperUser,
    };

    onClickSaveAndUpdate(payload);

    const userSelections = await getUserSelections(
      formValues,
      currentSelectedLanguage
    );

    adobeLinkTrackEvent({
      userSelections,
      name: getButtonTitle(),
      location: 'Set preferences',
    });
  };

  const onClearHandler = () => {
    const formFields = JSON.parse(JSON.stringify(form.getValues()));

    const enabledPreferences = countPreferences(formFields);
    setPreferenceCount(enabledPreferences);

    form.reset({
      distance: '',
      virtualApptAvbl: false,
      onlineApptSchdlAvbl: false,
      evngApptAvbl: false,
      wkndApptAvbl: false,
      languagesSpoken: [],
      providerGender: [],
      providerBoardCertStatus: false,
    });
    adobeLinkTrackEvent({
      name: 'clear all',
      location: 'Set preferences',
    });
  };

  const getButtonTitle = () => {
    if (preferencesFormFromProviderDetail) {
      return PREFERENCES_FORM_UPDATE_PROVIDER_SCORE?.title;
    }
    if (search) {
      return UPDATE_SEARCH_RESULTS?.title;
    }
    return PREFERENCES_FORM_SEARCH_PROVIDER?.title;
  };

  const [selectedLanguagesText, setSelectedLanguagesText] =
    useState<string>('');
  useEffect(() => {
    const fetchSelectedLanguages = async () => {
      if (formValues?.languagesSpoken?.length) {
        const selectedLanguages = await getSelectedLanguage(
          formValues?.languagesSpoken,
          currentSelectedLanguage
        );
        const formattedLanguages = selectedLanguages
          ?.map((item) => removeSpecialCharactersUsingMatch(item ?? ''))
          ?.join(', ');
        setSelectedLanguagesText(
          formattedLanguages || PREFERENCES_FORM_NO_PREFERENCES_SET?.title
        );
      } else {
        setSelectedLanguagesText(PREFERENCES_FORM_NO_PREFERENCES_SET?.title);
      }
    };

    fetchSelectedLanguages();
  }, [formValues?.languagesSpoken, currentSelectedLanguage]);

  const { portalData } = useContext(PortalContext);
  const isUmr = isUmrMember(portalData?.portalName);
  return (
    <React.Fragment>
      <StyledDrawer
        data-auto-testid="smart-choice-preferences-drawer"
        data-testid="smart-choice-preferences-drawer"
        isOpen={isOpen}
        onClose={() => {
          handleCloseDrawer();
          adobeLinkTrackEvent({
            name: 'Close',
            location: 'Set preferences',
          });
        }}
        position={properties.position}
        size={properties.drawerSize}
        title={
          <Text
            color="$primary"
            css={{ fontSize: '32.45px !important', fontWeight: 400 }}
            data-testid="smart-choice-preferences-heading"
          >
            {PREFERENCES_FORM_HEADING_DESCRIPTION?.title}
          </Text>
        }
      >
        <ContentWrapper
          style={{
            padding: mobileScreen ? '0px' : '16px',
            '& form': { marginLeft: '-16px', marginRight: '-16px' },
          }}
        >
          <Flex justify="space-between">
            <Text
              css={{ fontSize: '16px', fontWeight: 700, display: 'block' }}
              data-testid="set-preferences-sub-heading"
            >
              {`${preferenceCount} ${PREFERENCES_FORM_PREFERENCES_APPLIED?.title}`}
            </Text>
            <ClearAllButton
              data-testid="clear-all-preferences"
              onClick={onClearHandler}
              tabIndex={0}
            >
              <Text
                css={{
                  fontSize: '16px',
                  fontWeight: 700,
                  display: 'block',
                  color: '$interactive1',
                  cursor: 'pointer',
                }}
              >
                {PREFERENCES_FORM_CLEAR_ALL?.title}
              </Text>
            </ClearAllButton>
          </Flex>
          <StyledMarkdown
            css={{
              fontSize: '14px',
              fontWeight: 400,
              marginBottom: '16px',
              color: '$black !important',
            }}
            data-testid="set-preferences-sub-heading-desc"
          >
            {PREFERENCES_FORM_HEADING_DESCRIPTION?.message}
          </StyledMarkdown>

          <Flex>
            <HeadingText offset={2}>
              {PREFERENCES_FORM_CONVENIENCE_TITLE_DESC?.title}
            </HeadingText>
            <Text
              css={{
                marginBottom: '16px',
                fontSize: '16px',
              }}
            >
              {PREFERENCES_FORM_CONVENIENCE_TITLE_DESC?.message}
            </Text>
          </Flex>

          <FormProvider
            data-auto-testid="set-preferences-form"
            data-testid="set-preferences-form"
            onSubmit={onSubmit}
            state={form}
          >
            <AccordionContainer isCollapsible>
              <Accordion.Item value="distance">
                <Accordion.Trigger>
                  <Flex>
                    <Accordion.Header
                      data-testid="preferences-distance"
                      subText={
                        formValues?.distance
                          ? `${PREFERENCES_FORM_WITHIN?.title} ${
                              formValues?.distance
                            } ${
                              formValues?.distance && formValues?.distance > 1
                                ? PREFERENCES_FORM_MILES?.title
                                : PREFERENCES_FORM_MILE?.title
                            }`
                          : PREFERENCES_FORM_NO_PREFERENCES_SET?.title
                      }
                    >
                      <Heading offset={3}>
                        {PREFERENCES_FORM_DISTANCE_WITHIN?.title}
                      </Heading>
                    </Accordion.Header>
                  </Flex>
                </Accordion.Trigger>
                <Accordion.Content>
                  <RadioGroup label="" model="distance">
                    {distanceMiles?.map((item) => (
                      <RadioGroup.Radio
                        data-testid={`preferences-distance-miles`}
                        key={item}
                        label={`${item} ${
                          parseInt(item) > 1
                            ? PREFERENCES_FORM_MILES?.title
                            : PREFERENCES_FORM_MILE?.title
                        }`}
                        value={item}
                      />
                    ))}
                  </RadioGroup>
                </Accordion.Content>
              </Accordion.Item>
            </AccordionContainer>
            <Divider />
            <Flex>
              <ToggleSwitchField
                data-testid="preferences"
                label={PREFERENCES_FORM_VIRTUAL_APPOINTMENTS?.title as string}
                model="virtualApptAvbl"
              />
              <Divider />
              {!isUmr && (
                <>
                  <ToggleSwitchField
                    data-testid="preferences"
                    label={
                      PREFERENCES_FORM_ONLINE_APPOINTMENT_SCHEDULING?.title as string
                    }
                    model="onlineApptSchdlAvbl"
                  />
                  <Divider />
                </>
              )}
              <ToggleSwitchField
                data-testid="preferences"
                label={PREFERENCES_FORM_EVENING_APPOINTMENTS?.title as string}
                model="evngApptAvbl"
              />
              <Divider />
              <ToggleSwitchField
                data-testid="preferences"
                label={PREFERENCES_FORM_WEEKEND_APPOINTMENTS?.title as string}
                model="wkndApptAvbl"
              />
              {/* <Divider />
                  <ToggleSwitchField
                label={t('SMART_CHOICE.PREFERENCES.call_support') as string}
                    model="call_support"
              /> */}
              <Divider />
            </Flex>
            <Flex css={{ padding: '0px 16px' }}>
              <HeadingText offset={2}>
                {PREFERENCES_FORM_PERSONAL_FIT_TITLE_DESC?.title}
              </HeadingText>
              <Text
                css={{
                  marginBottom: '16px',
                  fontSize: '16px',
                }}
              >
                {PREFERENCES_FORM_PERSONAL_FIT_TITLE_DESC?.message}
              </Text>
            </Flex>
            <AccordionContainer isCollapsible>
              <Accordion.Item value="language">
                <Accordion.Trigger>
                  <Accordion.Header
                    data-testid="preferences-languages-spoken"
                    subText={
                      formValues?.languagesSpoken === ''
                        ? PREFERENCES_FORM_NO_PREFERENCES_SET?.title
                        : formValues?.languagesSpoken?.length
                        ? selectedLanguagesText
                        : PREFERENCES_FORM_NO_PREFERENCES_SET?.title
                    }
                  >
                    <Heading offset={3}>
                      {PREFERENCES_FORM_LANGUAGES_SPOKEN?.title}
                    </Heading>
                  </Accordion.Header>
                </Accordion.Trigger>
                <Accordion.Content>
                  <CheckboxGroup
                    data-testid="language-values-box"
                    label=""
                    model="languagesSpoken"
                  >
                    <VirtualizedListCheckbox
                      data={languagesSpoken}
                      form={form}
                    />
                  </CheckboxGroup>
                </Accordion.Content>
              </Accordion.Item>
            </AccordionContainer>
            <Divider />
            <AccordionContainer isCollapsible>
              <Accordion.Item value="gender">
                <Accordion.Trigger>
                  <Accordion.Header
                    data-testid="preferences-gender"
                    subText={
                      formValues?.providerGender === ''
                        ? PREFERENCES_FORM_NO_PREFERENCES_SET?.title
                        : formValues?.providerGender?.length
                        ? formValues.providerGender
                            .map((value) => {
                              const genderItem = genderList.find(
                                (item) => item.value === value
                              );
                              return genderItem ? t(genderItem.title) : '';
                            })
                            .filter((item) => item !== '')
                            .join(', ')
                        : PREFERENCES_FORM_NO_PREFERENCES_SET?.title
                    }
                  >
                    <Heading offset={3}>
                      {PREFERENCES_FORM_GENDER?.title}
                    </Heading>
                  </Accordion.Header>
                </Accordion.Trigger>
                <Accordion.Content>
                  <CheckboxGroup label="" model="providerGender">
                    {genderList.map((item) => (
                      <Checkbox
                        data-testid="preferences-gender-checkbox"
                        key={item?.value}
                        label={t(item?.title) as string}
                        value={item?.value}
                      />
                    ))}
                  </CheckboxGroup>
                </Accordion.Content>
              </Accordion.Item>
            </AccordionContainer>
            <Divider />
            <ToggleSwitchField
              data-testid="preferences"
              label={PREFERENCES_FORM_BOARD_CERTIFIED?.title as string}
              model="providerBoardCertStatus"
            />
            <Divider />
            <Flex
              css={{
                position: 'sticky',
                bottom: '0',
                padding: '16px',
                zIndex: 1,
                backgroundColor: '$white',
              }}
            >
              <Button
                css={{ width: '100%' }}
                data-testid="search-for-provider-button"
                isDisabled={disabledSubmit === 0}
                type="submit"
              >
                {getButtonTitle()}
              </Button>
            </Flex>
          </FormProvider>
        </ContentWrapper>
      </StyledDrawer>
    </React.Fragment>
  );
};
